import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Crosshair, 
  Plus, 
  Trash2, 
  Search,
  CheckCircle,
  XCircle,
  Edit,
  Users,
  Clock,
  Calendar,
  Settings,
  X
} from "lucide-react";

const MemberWeaponsPage = () => {
  const { token, user, hasRole } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberWeapons, setMemberWeapons] = useState([]);
  const [memberShootingTest, setMemberShootingTest] = useState(null);
  const [weaponDialogOpen, setWeaponDialogOpen] = useState(false);
  const [shootingDialogOpen, setShootingDialogOpen] = useState(false);
  
  // Preizkus termini state
  const [preizkusEvents, setPreizkusEvents] = useState([]);
  const [selectedPreizkusEvent, setSelectedPreizkusEvent] = useState(null);
  const [terminiData, setTerminiData] = useState(null);
  const [terminiLoading, setTerminiLoading] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [newPreizkusDialogOpen, setNewPreizkusDialogOpen] = useState(false);
  const [adminReserveDialogOpen, setAdminReserveDialogOpen] = useState(false);
  const [selectedTerminForAdmin, setSelectedTerminForAdmin] = useState(null);
  const [selectedUserForReserve, setSelectedUserForReserve] = useState("");
  const [allMembers, setAllMembers] = useState([]);
  const [setupForm, setSetupForm] = useState({
    zacetek: "08:00",
    konec: "16:00",
    interval_minut: 15
  });
  const [newPreizkusForm, setNewPreizkusForm] = useState({
    naslov: "Preizkus orožja",
    datum: "",
    cas: "08:00",
    lokacija: "Strelišče Rakek",
    opis: "Obvezen preizkus orožja za člane"
  });
  
  const [weaponForm, setWeaponForm] = useState({
    vrsta: "",
    znamka: "",
    model: "",
    kaliber: "",
    serijska_stevilka: "",
    opombe: ""
  });
  
  const [shootingForm, setShootingForm] = useState({
    pristrel_opravljen: false,
    pristrel_datum: "",
    pristrel_opombe: ""
  });

  const weaponTypes = [
    { value: "puska", label: "Puška" },
    { value: "pistola", label: "Pištola" },
    { value: "kombinirano", label: "Kombinirano orožje" },
    { value: "karabin", label: "Karabin" },
    { value: "malokalibrska", label: "Malokalibrska puška" },
    { value: "drugo", label: "Drugo" }
  ];

  // Check if user can manage all members (predsednik, gospodar, referent_strelstvo)
  const canManageAll = hasRole(["administrator", "predsednik", "gospodar", "referent_strelstvo"]);
  
  // Check if viewing own profile - members can edit their own weapons
  const isOwnProfile = selectedMember?.id === user?.id;

  useEffect(() => {
    fetchPreizkusEvents();
    if (canManageAll) {
      fetchMembers();
      fetchAllMembers(); // For admin reservation dropdown
    } else {
      // Regular member - show only their own weapons
      setSelectedMember(user);
      fetchMemberDetails(user);
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllMembers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      setAllMembers(response.data || []);
    } catch (error) {
      console.error("Error fetching all members:", error);
    }
  };

  const fetchPreizkusEvents = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/preizkus/events`, { headers });
      setPreizkusEvents(response.data || []);
    } catch (error) {
      console.error("Error fetching preizkus events:", error);
      // Don't show error toast, just log it
    }
  };

  const fetchTermini = async (eventId) => {
    setTerminiLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/preizkus/termini/${eventId}`, { headers });
      setTerminiData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setTerminiData(null);
      } else {
        toast.error("Napaka pri nalaganju terminov");
      }
    } finally {
      setTerminiLoading(false);
    }
  };

  const handleSetupTermini = async () => {
    if (!selectedPreizkusEvent) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/preizkus/setup`, {
        event_id: selectedPreizkusEvent.id,
        ...setupForm
      }, { headers });
      toast.success("Termini nastavljeni");
      setSetupDialogOpen(false);
      fetchTermini(selectedPreizkusEvent.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri nastavitvi terminov");
    }
  };

  const handleRezervirajTermin = async (termin) => {
    if (!selectedPreizkusEvent) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/preizkus/rezervacija`, {
        event_id: selectedPreizkusEvent.id,
        termin: termin
      }, { headers });
      toast.success(`Termin ${termin} rezerviran`);
      fetchTermini(selectedPreizkusEvent.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri rezervaciji");
    }
  };

  const handleAdminReserveTermin = async () => {
    if (!selectedUserForReserve || !selectedTerminForAdmin || !selectedPreizkusEvent) {
      toast.error("Izberite člana in termin");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const selectedMemberData = allMembers.find(m => m.id === selectedUserForReserve);
      
      await axios.post(`${API}/preizkus/rezervacija`, {
        event_id: selectedPreizkusEvent.id,
        termin: selectedTerminForAdmin,
        user_id: selectedUserForReserve
      }, { headers });
      toast.success(`Termin ${selectedTerminForAdmin} rezerviran za ${selectedMemberData?.ime} ${selectedMemberData?.priimek}`);
      setAdminReserveDialogOpen(false);
      setSelectedUserForReserve("");
      setSelectedTerminForAdmin(null);
      fetchTermini(selectedPreizkusEvent.id);
    } catch (error) {
      console.error("Reservation error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Napaka pri rezervaciji");
    }
  };

  const handleCreatePreizkus = async () => {
    if (!newPreizkusForm.datum) {
      toast.error("Izberite datum preizkusa");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/events`, {
        naslov: newPreizkusForm.naslov,
        datum: newPreizkusForm.datum,
        cas: newPreizkusForm.cas,
        lokacija: newPreizkusForm.lokacija,
        opis: newPreizkusForm.opis,
        tip: "preizkus"
      }, { headers });
      
      toast.success("Preizkus ustvarjen");
      setNewPreizkusDialogOpen(false);
      
      // Refresh events and select the new one
      await fetchPreizkusEvents();
      setSelectedPreizkusEvent(response.data);
      
      // Reset form
      setNewPreizkusForm({
        naslov: "Preizkus orožja",
        datum: "",
        cas: "08:00",
        lokacija: "Strelišče Rakek",
        opis: "Obvezen preizkus orožja za člane"
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri ustvarjanju preizkusa");
    }
  };

  const handlePrkliciRezervacijo = async () => {
    if (!selectedPreizkusEvent) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/preizkus/rezervacija/${selectedPreizkusEvent.id}`, { headers });
      toast.success("Rezervacija preklicana");
      fetchTermini(selectedPreizkusEvent.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri preklicu");
    }
  };

  const handleAdminPrkliciRezervacijo = async (userId) => {
    if (!selectedPreizkusEvent) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/preizkus/rezervacija/${selectedPreizkusEvent.id}/${userId}`, { headers });
      toast.success("Rezervacija preklicana");
      fetchTermini(selectedPreizkusEvent.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri preklicu");
    }
  };

  const fetchMembers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      setMembers(response.data);
    } catch (error) {
      toast.error("Napaka pri nalaganju članov");
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberDetails = async (member) => {
    if (!member) return;
    setSelectedMember(member);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [weaponsRes, shootingRes] = await Promise.all([
        axios.get(`${API}/users/${member.id}/weapons`, { headers }),
        axios.get(`${API}/users/${member.id}/shooting-test`, { headers })
      ]);
      setMemberWeapons(weaponsRes.data);
      setMemberShootingTest(shootingRes.data);
      setShootingForm({
        pristrel_opravljen: shootingRes.data.pristrel_opravljen || false,
        pristrel_datum: shootingRes.data.pristrel_datum || "",
        pristrel_opombe: shootingRes.data.pristrel_opombe || ""
      });
    } catch (error) {
      console.error("Error fetching member details:", error);
      setMemberWeapons([]);
      setMemberShootingTest({ pristrel_opravljen: false });
    }
  };

  const handleAddWeapon = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/users/${selectedMember.id}/weapons`, weaponForm, { headers });
      toast.success("Orožje dodano");
      setWeaponDialogOpen(false);
      setWeaponForm({ vrsta: "", znamka: "", model: "", kaliber: "", serijska_stevilka: "", opombe: "" });
      fetchMemberDetails(selectedMember);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri dodajanju");
    }
  };

  const handleDeleteWeapon = async (weaponId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/weapons/${weaponId}`, { headers });
      toast.success("Orožje izbrisano");
      fetchMemberDetails(selectedMember);
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  const handleUpdateShootingTest = async () => {
    if (!selectedMember) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/users/${selectedMember.id}/shooting-test`, shootingForm, { headers });
      toast.success("Pristrel posodobljen");
      setShootingDialogOpen(false);
      fetchMemberDetails(selectedMember);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri posodabljanju");
    }
  };

  const getWeaponTypeLabel = (type) => {
    return weaponTypes.find(t => t.value === type)?.label || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("sl-SI", { day: "numeric", month: "short", year: "numeric" });
  };

  const filteredMembers = members.filter(m => {
    const fullName = `${m.ime} ${m.priimek}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Helper to get user's reservation from termini
  const getUserReservation = () => {
    if (!terminiData?.termini) return null;
    return terminiData.termini.find(t => t.moja_rezervacija);
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="weapons-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <Crosshair className="h-6 w-6 sm:h-8 sm:w-8" />
          {canManageAll ? "Orožje in pristrel" : "Moje orožje"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {canManageAll ? "Evidenca orožja članov in status pristrelitve" : "Pregled mojega orožja in statusa pristrelitve"}
        </p>
      </div>

      {/* Preizkus orožja - Termini Section */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                <Calendar className="h-4 w-4" />
                Preizkus orožja in strelca - Rezervacija termina
              </CardTitle>
              <CardDescription>
                {preizkusEvents.length > 0 
                  ? "Izberite dan preizkusa in rezervirajte svoj termin"
                  : "Ni razpisanih preizkusov orožja"}
              </CardDescription>
            </div>
            
            {/* Add new preizkus button - only for managers */}
            {canManageAll && (
              <Dialog open={newPreizkusDialogOpen} onOpenChange={setNewPreizkusDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="h-4 w-4 mr-1" />
                    Nov preizkus
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md mx-4">
                  <DialogHeader>
                    <DialogTitle>Razpiši nov preizkus orožja</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Naslov</Label>
                      <Input
                        value={newPreizkusForm.naslov}
                        onChange={(e) => setNewPreizkusForm({ ...newPreizkusForm, naslov: e.target.value })}
                        placeholder="Preizkus orožja"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Datum *</Label>
                        <Input
                          type="date"
                          value={newPreizkusForm.datum}
                          onChange={(e) => setNewPreizkusForm({ ...newPreizkusForm, datum: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Ura začetka</Label>
                        <Input
                          type="time"
                          value={newPreizkusForm.cas}
                          onChange={(e) => setNewPreizkusForm({ ...newPreizkusForm, cas: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Lokacija</Label>
                      <Input
                        value={newPreizkusForm.lokacija}
                        onChange={(e) => setNewPreizkusForm({ ...newPreizkusForm, lokacija: e.target.value })}
                        placeholder="Strelišče Rakek"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Opis</Label>
                      <Textarea
                        value={newPreizkusForm.opis}
                        onChange={(e) => setNewPreizkusForm({ ...newPreizkusForm, opis: e.target.value })}
                        placeholder="Obvezen preizkus orožja..."
                        rows={2}
                      />
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setNewPreizkusDialogOpen(false)}>
                      Prekliči
                    </Button>
                    <Button onClick={handleCreatePreizkus} className="bg-amber-600 hover:bg-amber-700">
                      Ustvari preizkus
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {preizkusEvents.length > 0 ? (
            <>
              {/* Event Selection */}
              <div className="flex flex-wrap gap-2">
                {preizkusEvents.map((event) => (
                  <Button
                    key={event.id}
                    variant={selectedPreizkusEvent?.id === event.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSelectedPreizkusEvent(event);
                      fetchTermini(event.id);
                      // Pre-fill setup form if setup exists
                      if (event.termin_setup) {
                        setSetupForm({
                          zacetek: event.termin_setup.zacetek || "08:00",
                          konec: event.termin_setup.konec || "16:00",
                          interval_minut: event.termin_setup.interval_minut || 15
                        });
                      }
                    }}
                    className={selectedPreizkusEvent?.id === event.id ? "bg-amber-600 hover:bg-amber-700" : ""}
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    {formatDate(event.datum)}
                  </Button>
                ))}
              </div>

              {/* Selected Event Details & Termini */}
              {selectedPreizkusEvent && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{selectedPreizkusEvent.naslov}</h4>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(selectedPreizkusEvent.datum)} • {selectedPreizkusEvent.lokacija}
                      </p>
                    </div>
                  
                  {/* Setup Button - Only for managers */}
                  {canManageAll && (
                    <div className="flex gap-2">
                      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Settings className="h-4 w-4 mr-1" />
                            Nastavi termine
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-sm mx-4">
                          <DialogHeader>
                            <DialogTitle>Nastavi termine preizkusa</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Začetek</Label>
                                <Input
                                  type="time"
                                  value={setupForm.zacetek}
                                  onChange={(e) => setSetupForm({ ...setupForm, zacetek: e.target.value })}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Konec</Label>
                                <Input
                                  type="time"
                                  value={setupForm.konec}
                                  onChange={(e) => setSetupForm({ ...setupForm, konec: e.target.value })}
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Interval (minute)</Label>
                              <Select 
                                value={String(setupForm.interval_minut)}
                                onValueChange={(val) => setSetupForm({ ...setupForm, interval_minut: parseInt(val) })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="10">10 minut</SelectItem>
                                  <SelectItem value="15">15 minut</SelectItem>
                                  <SelectItem value="20">20 minut</SelectItem>
                                  <SelectItem value="30">30 minut</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setSetupDialogOpen(false)}>
                              Prekliči
                            </Button>
                            <Button onClick={handleSetupTermini} className="bg-amber-600 hover:bg-amber-700">
                              Shrani
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      
                      {/* Admin Reserve for Member Button */}
                      <Dialog open={adminReserveDialogOpen} onOpenChange={setAdminReserveDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Users className="h-4 w-4 mr-1" />
                            Rezerviraj za člana
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md mx-4">
                          <DialogHeader>
                            <DialogTitle>Rezerviraj termin za člana</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="space-y-1">
                              <Label className="text-xs">Izberi člana</Label>
                              <Select 
                                value={selectedUserForReserve}
                                onValueChange={setSelectedUserForReserve}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Izberi člana..." />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {allMembers
                                    .filter(m => {
                                      // Exclude members who already have a reservation
                                      const hasReservation = terminiData?.termini?.some(t => t.zaseden && t.user_id === m.id);
                                      return !hasReservation;
                                    })
                                    .sort((a, b) => a.priimek.localeCompare(b.priimek))
                                    .map((member) => (
                                      <SelectItem key={member.id} value={member.id}>
                                        {member.priimek} {member.ime}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Izberi termin</Label>
                              <Select 
                                value={selectedTerminForAdmin || ""}
                                onValueChange={setSelectedTerminForAdmin}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Izberi termin..." />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {terminiData?.termini
                                    ?.filter(t => !t.zaseden)
                                    .map((slot) => (
                                      <SelectItem key={slot.termin} value={slot.termin}>
                                        {slot.termin}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => {
                              setAdminReserveDialogOpen(false);
                              setSelectedUserForReserve("");
                              setSelectedTerminForAdmin(null);
                            }}>
                              Prekliči
                            </Button>
                            <Button 
                              onClick={handleAdminReserveTermin} 
                              className="bg-amber-600 hover:bg-amber-700"
                              disabled={!selectedUserForReserve || !selectedTerminForAdmin}
                            >
                              Rezerviraj
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>

                {/* Termini Grid */}
                {terminiLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="spinner" />
                  </div>
                ) : terminiData ? (
                  <div className="space-y-3">
                    {/* User's current reservation */}
                    {getUserReservation() && (
                      <div className="p-3 rounded-lg bg-green-100 border border-green-300">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-medium text-green-800">
                              Vaš termin: {getUserReservation().termin}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePrkliciRezervacijo}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Prekliči
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Available slots grid */}
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                      {terminiData.termini.map((slot) => {
                        const isMyReservation = slot.moja_rezervacija;
                        const isTaken = slot.zaseden && !isMyReservation;
                        const isFree = !slot.zaseden;
                        
                        let slotClasses = "relative p-2 rounded text-center text-sm transition-all ";
                        if (isMyReservation) {
                          slotClasses += "bg-green-500 text-white font-medium";
                        } else if (isTaken) {
                          slotClasses += "bg-red-100 text-red-400 cursor-not-allowed border-2 border-red-200";
                        } else {
                          slotClasses += "bg-white border border-gray-200 hover:border-amber-400 hover:bg-amber-50 cursor-pointer";
                        }
                        
                        return (
                          <div
                            key={slot.termin}
                            className={slotClasses}
                            onClick={() => {
                              if (isFree && !getUserReservation()) {
                                handleRezervirajTermin(slot.termin);
                              }
                            }}
                            title={
                              isMyReservation
                                ? 'Vaš termin'
                                : isTaken
                                ? (terminiData.can_see_all ? `Zasedeno: ${slot.user_ime}` : 'Zasedeno')
                                : 'Prosto - kliknite za rezervacijo'
                            }
                          >
                            <Clock className="h-3 w-3 mx-auto mb-1 opacity-50" />
                            <span className={isTaken ? "line-through" : ""}>{slot.termin}</span>
                            
                            {/* Admin: show who reserved and allow cancel */}
                            {terminiData.can_see_all && isTaken && (
                              <div className="absolute -top-1 -right-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-5 w-5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAdminPrkliciRezervacijo(slot.user_id);
                                  }}
                                  title={`Prekliči rezervacijo: ${slot.user_ime}`}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2 border-t">
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-white border border-gray-200" />
                        <span>Prosto</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-green-500" />
                        <span>Vaš termin</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-200" />
                        <span>Zasedeno</span>
                      </div>
                    </div>

                    {/* Manager view: All reservations list */}
                    {terminiData.can_see_all && (
                      <div className="mt-4 pt-4 border-t">
                        <h5 className="font-medium text-sm mb-2">Vse rezervacije ({terminiData.termini.filter(t => t.zaseden).length})</h5>
                        <div className="space-y-1 max-h-40 overflow-auto">
                          {terminiData.termini
                            .filter(t => t.zaseden)
                            .sort((a, b) => a.termin.localeCompare(b.termin))
                            .map((slot) => (
                              <div key={slot.termin} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50">
                                <span className="font-medium">{slot.termin}</span>
                                <span>{slot.user_ime}</span>
                              </div>
                            ))}
                          {terminiData.termini.filter(t => t.zaseden).length === 0 && (
                            <p className="text-muted-foreground text-sm">Še ni rezervacij</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 border rounded-lg bg-white">
                    <Clock className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Termini za ta dan še niso nastavljeni
                    </p>
                    {canManageAll && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Kliknite "Nastavi termine" za nastavitev
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            </>
          ) : (
            <div className="text-center py-6">
              <Calendar className="h-12 w-12 text-amber-300 mx-auto mb-3" />
              <p className="text-muted-foreground">Ni razpisanih preizkusov orožja</p>
              {canManageAll && (
                <p className="text-sm text-muted-foreground mt-1">
                  Kliknite "Nov preizkus" za razpis novega datuma
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className={`grid grid-cols-1 ${canManageAll ? 'lg:grid-cols-3' : ''} gap-4`}>
        {/* Members List - Only for managers */}
        {canManageAll && (
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Člani
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Išči člana..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <div className="max-h-[500px] overflow-auto space-y-1">
                {filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className={`p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedMember?.id === member.id 
                        ? 'bg-primary/10 border border-primary/30' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => fetchMemberDetails(member)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{member.ime} {member.priimek}</span>
                      {member.pristrel_opravljen && (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Member Details */}
        <Card className={canManageAll ? "lg:col-span-2" : ""}>
          {selectedMember ? (
            <>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{selectedMember.ime} {selectedMember.priimek}</CardTitle>
                    <CardDescription>Član #{selectedMember.stevilka_clana}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {memberShootingTest?.pristrel_opravljen ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Pristrel opravljen
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        <XCircle className="h-3 w-3 mr-1" />
                        Pristrel ni opravljen
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Shooting Test Section */}
                <div className="p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Crosshair className="h-4 w-4" />
                      Pristrelitev orožja
                    </h3>
                    {canManageAll && (
                      <Dialog open={shootingDialogOpen} onOpenChange={setShootingDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline">
                            <Edit className="h-4 w-4 mr-1" />
                            Uredi
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-sm mx-4">
                          <DialogHeader>
                            <DialogTitle>Uredi pristrel</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="pristrel"
                                checked={shootingForm.pristrel_opravljen}
                                onCheckedChange={(checked) => 
                                  setShootingForm({ ...shootingForm, pristrel_opravljen: checked })
                                }
                              />
                              <Label htmlFor="pristrel">Pristrel opravljen</Label>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Datum pristrelitve</Label>
                              <Input
                                type="date"
                                value={shootingForm.pristrel_datum}
                                onChange={(e) => setShootingForm({ ...shootingForm, pristrel_datum: e.target.value })}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Opombe</Label>
                              <Textarea
                                value={shootingForm.pristrel_opombe}
                                onChange={(e) => setShootingForm({ ...shootingForm, pristrel_opombe: e.target.value })}
                                placeholder="Dodatne opombe..."
                                rows={2}
                              />
                            </div>
                          </div>
                          <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setShootingDialogOpen(false)}>
                              Prekliči
                            </Button>
                            <Button onClick={handleUpdateShootingTest}>Shrani</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <span className={`ml-2 font-medium ${memberShootingTest?.pristrel_opravljen ? 'text-green-600' : 'text-amber-600'}`}>
                        {memberShootingTest?.pristrel_opravljen ? 'Opravljen' : 'Ni opravljen'}
                      </span>
                    </div>
                    {memberShootingTest?.pristrel_opravljen && memberShootingTest?.pristrel_datum && (
                      <div>
                        <span className="text-muted-foreground">Datum:</span>
                        <span className="ml-2">{formatDate(memberShootingTest.pristrel_datum)}</span>
                      </div>
                    )}
                    {memberShootingTest?.pristrel_opravljen && memberShootingTest?.pristrel_opombe && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Opombe:</span>
                        <span className="ml-2">{memberShootingTest.pristrel_opombe}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Weapons Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">Evidenca orožja</h3>
                    {(canManageAll || isOwnProfile) && (
                      <Dialog open={weaponDialogOpen} onOpenChange={setWeaponDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" data-testid="add-weapon-btn">
                            <Plus className="h-4 w-4 mr-1" />
                            Dodaj
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-sm mx-4">
                          <DialogHeader>
                            <DialogTitle>Dodaj orožje</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleAddWeapon} className="space-y-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Vrsta orožja *</Label>
                              <Select 
                                onValueChange={(value) => setWeaponForm({ ...weaponForm, vrsta: value })} 
                                value={weaponForm.vrsta}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Izberite vrsto" />
                                </SelectTrigger>
                                <SelectContent>
                                  {weaponTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Znamka *</Label>
                                <Input
                                  value={weaponForm.znamka}
                                  onChange={(e) => setWeaponForm({ ...weaponForm, znamka: e.target.value })}
                                  placeholder="npr. Sauer, Beretta"
                                  required
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Model</Label>
                                <Input
                                  value={weaponForm.model}
                                  onChange={(e) => setWeaponForm({ ...weaponForm, model: e.target.value })}
                                  placeholder="npr. 404, 686"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Kaliber *</Label>
                                <Input
                                  value={weaponForm.kaliber}
                                  onChange={(e) => setWeaponForm({ ...weaponForm, kaliber: e.target.value })}
                                  placeholder="npr. .308 Win"
                                  required
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Serijska št.</Label>
                                <Input
                                  value={weaponForm.serijska_stevilka}
                                  onChange={(e) => setWeaponForm({ ...weaponForm, serijska_stevilka: e.target.value })}
                                  placeholder="opcijsko"
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Opombe</Label>
                              <Textarea
                                value={weaponForm.opombe}
                                onChange={(e) => setWeaponForm({ ...weaponForm, opombe: e.target.value })}
                                placeholder="Dodatne opombe..."
                                rows={2}
                              />
                            </div>
                            <DialogFooter className="gap-2">
                              <Button type="button" variant="outline" onClick={() => setWeaponDialogOpen(false)}>
                                Prekliči
                              </Button>
                              <Button type="submit" disabled={!weaponForm.vrsta || !weaponForm.znamka || !weaponForm.kaliber}>
                                Dodaj
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                  
                  {memberWeapons.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg">
                      <Crosshair className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Ni evidentiranega orožja</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {memberWeapons.map((weapon) => (
                        <div key={weapon.id} className="p-3 rounded-lg border bg-card">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline">{getWeaponTypeLabel(weapon.vrsta)}</Badge>
                                {weapon.znamka && (
                                  <span className="font-semibold">{weapon.znamka}</span>
                                )}
                                {weapon.model && (
                                  <span className="text-muted-foreground">{weapon.model}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-sm">
                                <span className="font-medium">{weapon.kaliber}</span>
                                {weapon.serijska_stevilka && (
                                  <span className="text-muted-foreground">
                                    Ser. št.: {weapon.serijska_stevilka}
                                  </span>
                                )}
                              </div>
                              {weapon.opombe && (
                                <p className="text-xs text-muted-foreground mt-1">{weapon.opombe}</p>
                              )}
                            </div>
                            {(canManageAll || isOwnProfile) && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-destructive"
                                onClick={() => handleDeleteWeapon(weapon.id)}
                                data-testid={`delete-weapon-${weapon.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </>
          ) : (
            <CardContent className="py-12 text-center">
              <Crosshair className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">Izberite člana za pregled podatkov</p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MemberWeaponsPage;
